import { FileText, Gauge, Info, Languages, Sparkles, Workflow } from 'lucide-react'

import type { ArtifactResponse, SummaryDetailLevel } from '@/client/types.gen'
import type { AnalysisLanguage } from '@/components/common/language-switcher'
import { Button as UIButton } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTranslation } from '@/hooks/useTranslation'
import { TOUR } from '@/lib/product-tour/constants'
import { cn } from '@/lib/utils'
import { type BpmnGenerationOptionsState, listEnabledBpmnOptionKeys } from './bpmnOptions'
import type { StepComponentProps } from './types'
import { formatArtifactLabel } from './utils'

interface ReviewStepProps extends StepComponentProps {
	primaryFiles: ArtifactResponse[]
	contextFiles: ArtifactResponse[]
	selectedTemplate: string | null
	summaryDetailLevel: SummaryDetailLevel
	generationUserInstructions: string
	analysisLanguage: AnalysisLanguage
	bpmnGenerationOptions: BpmnGenerationOptionsState
	isGenerating: boolean
	onGenerate: () => void
}

export function ReviewStep({
	primaryFiles,
	contextFiles,
	selectedTemplate,
	summaryDetailLevel,
	generationUserInstructions,
	analysisLanguage,
	bpmnGenerationOptions,
	isGenerating,
	onGenerate,
}: ReviewStepProps) {
	const { t } = useTranslation()

	const languageLabelKey = `languages.${analysisLanguage}`
	const languageLabel = t(languageLabelKey as never)

	const summaryDetailLevelLabel = t(
		`pages.summaryDetailLevel.options.${summaryDetailLevel}.label` as never
	)
	const enabledBpmnOptions = listEnabledBpmnOptionKeys(bpmnGenerationOptions)
	const trimmedInstructions = generationUserInstructions.trim()

	return (
		<div className="flex-1 flex flex-col min-h-0 space-y-4">
			<div>
				<div className="flex items-center gap-2 mb-1.5">
					<h2 className="text-lg font-semibold text-foreground">
						{t('pages.projectDetail.modal.step4.title')}
					</h2>
					<Tooltip>
						<TooltipTrigger asChild>
							<Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
						</TooltipTrigger>
						<TooltipContent>
							<p>{t('pages.projectDetail.modal.step4.tooltip')}</p>
						</TooltipContent>
					</Tooltip>
				</div>
			</div>

			<div className="flex-1 overflow-y-auto overflow-x-hidden px-1 py-1 space-y-0">
				{primaryFiles.length > 0 && (
					<div className="p-3">
						<p className="text-xs font-semibold text-primary uppercase tracking-wide mb-1.5">
							{t('pages.projectDetail.modal.step4.primaryFiles', { count: primaryFiles.length })}
						</p>
						<div className="space-y-1.5">
							{primaryFiles.map((artifact, i) => (
								<div key={artifact.id ?? i} className={cn('flex flex-col gap-0.5 py-1', i > 0)}>
									<p className="flex items-center gap-1.5 text-sm text-foreground">
										<FileText className="h-4 w-4 shrink-0" />
										<span>{artifact.name}</span>
									</p>
									{formatArtifactLabel(artifact, t) && (
										<span className="text-xs text-muted-foreground pl-5">
											{formatArtifactLabel(artifact, t)}
										</span>
									)}
								</div>
							))}
						</div>
					</div>
				)}

				{contextFiles.length > 0 && (
					<div className={cn('p-3', primaryFiles.length > 0 && 'border-t border-border')}>
						<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
							{t('pages.projectDetail.modal.step4.contextFiles', {
								count: contextFiles.length,
							})}
						</p>
						<div className="space-y-1.5">
							{contextFiles.map((artifact, i) => (
								<div key={artifact.id ?? i} className={cn('flex flex-col gap-0.5 py-1', i > 0)}>
									<p className="flex items-center gap-1.5 text-sm text-foreground">
										<FileText className="h-4 w-4 shrink-0" />
										<span>{artifact.name}</span>
									</p>
									{formatArtifactLabel(artifact, t) && (
										<span className="text-xs text-muted-foreground pl-5">
											{formatArtifactLabel(artifact, t)}
										</span>
									)}
								</div>
							))}
						</div>
					</div>
				)}

				<div
					className={cn(
						'p-3 space-y-1.5',
						(primaryFiles.length > 0 || contextFiles.length > 0) && 'border-t border-border'
					)}
				>
					<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
						{t('pages.projectDetail.modal.step4.template')}
					</p>
					<p className="flex items-center gap-1.5 text-sm text-foreground">
						<Workflow className="h-4 w-4" />
						<span>
							{selectedTemplate === 'bpmn'
								? t('pages.projectDetail.modal.bpmn')
								: t('pages.projectDetail.modal.noTemplateSelected')}
						</span>
					</p>
				</div>

				<div
					className={cn(
						'p-3 space-y-1.5',
						(primaryFiles.length > 0 || contextFiles.length > 0 || selectedTemplate) &&
							'border-t border-border'
					)}
				>
					<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
						{t('pages.projectDetail.modal.step4.outputLanguage')}
					</p>
					<p className="flex items-center gap-1.5 text-sm text-foreground">
						<Languages className="h-4 w-4" />
						<span>{languageLabel}</span>
					</p>
				</div>

				<div
					className={cn(
						'p-3 space-y-1.5',
						(primaryFiles.length > 0 || contextFiles.length > 0 || selectedTemplate) &&
							'border-t border-border'
					)}
				>
					<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
						{t('pages.projectDetail.modal.step4.summaryDetailLevel')}
					</p>
					<p className="flex items-center gap-1.5 text-sm text-foreground">
						<Gauge className="h-4 w-4 shrink-0" />
						<span>{summaryDetailLevelLabel}</span>
					</p>
				</div>

				<div
					className={cn(
						'p-3 space-y-1.5',
						(primaryFiles.length > 0 || contextFiles.length > 0 || selectedTemplate) &&
							'border-t border-border'
					)}
				>
					<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
						{t('pages.projectDetail.modal.step4.bpmnOptions')}
					</p>
					{enabledBpmnOptions.length === 0 ? (
						<p className="text-sm text-muted-foreground">
							{t('pages.projectDetail.modal.bpmnOptions.noneEnabled')}
						</p>
					) : (
						<ul className="space-y-1 text-sm text-foreground">
							{enabledBpmnOptions.map((optionKey) => (
								<li key={optionKey}>
									{t(`pages.projectDetail.modal.bpmnOptions.options.${optionKey}.label` as never)}
								</li>
							))}
						</ul>
					)}
				</div>

				{trimmedInstructions && (
					<div
						className={cn(
							'p-3 space-y-1.5',
							(primaryFiles.length > 0 || contextFiles.length > 0 || selectedTemplate) &&
								'border-t border-border'
						)}
					>
						<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
							{t('pages.projectDetail.modal.step4.customInstructions')}
						</p>
						<p className="text-sm text-foreground whitespace-pre-wrap">{trimmedInstructions}</p>
					</div>
				)}
			</div>

			<div className="flex gap-2">
				<UIButton
					type="button"
					onClick={onGenerate}
					disabled={!selectedTemplate || isGenerating || primaryFiles.length === 0}
					aria-busy={isGenerating}
					className="flex-[2]"
					data-tour={TOUR.modalGenerate}
				>
					{isGenerating ? (
						<>
							<Spinner className="w-4 h-4 inline mr-2" />
							{t('pages.projectDetail.modal.generateAnalysisInProgress')}
						</>
					) : (
						<>
							<Sparkles className="w-4 h-4 inline mr-2" />
							{t('pages.projectDetail.modal.generateAnalysis')}
						</>
					)}
				</UIButton>
			</div>
		</div>
	)
}
